import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Car, Plus, X, Camera, Loader, Trash2, ExternalLink, Save, RefreshCw,
  AlertTriangle, Paperclip, Sparkles, ShieldAlert, FileText, Wand2, Mail,
  MessageSquarePlus, Download, ClipboardCheck, ClipboardPaste,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { uprightJpegBytes } from '../utils/imageOrient';
import {
  buildAccidentSummaryPdfBytes, fillAllianzForm,
  base64ToBytes, bytesToBase64, isClm002Pdf, INSURED_NAME, POLICY_NO, CLM002_URL,
} from '../utils/accidentReport';

// ── Vehicle Accident / Insurance Claims ───────────────────────────────────────
// Admin-only register of vehicle accidents & insurance claims (Allianz Motor Fleet —
// see INSURED_NAME/POLICY_NO). Each claim links to a fleet vehicle + the driver at the wheel,
// captures the incident + third-party detail, retains photos & PDFs in a private
// bucket, carries the Allianz claim number + status, and keeps an append-only
// timeline. Drivers can lodge an initial report from the portal; it lands here as a
// 'reported' claim. The Allianz PDF is uploaded then filled in-engine; a branded
// summary PDF is generated alongside. Email to Allianz is PREPARED here and sent by
// the admin from their own mail client.

const CLAIMS_URL = '/.netlify/functions/vehicle-claims-sb';
const ASSIGN_URL = '/.netlify/functions/fleet-assignments-sb';
// Claims go to MARSH, our broker — they lodge with Allianz on our behalf. Confirmed by
// Paul 2026-07-17 (he was retyping this on every claim). Still editable in the dialog.
const CLAIMS_EMAIL = 'MailContractors.Insurance@marsh.com';
const MAX_MB = 4; // base64 inflates ~33%; Netlify function body limit is 6 MB

const claimsApi = (action, payload = {}) =>
  fetch(CLAIMS_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());
const assignApi = (action, payload = {}) =>
  fetch(ASSIGN_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());

const todayLocalISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtDate = (iso) => (iso ? new Date(iso + 'T00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtWhen = (ts) => (ts ? new Date(ts).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

function arrayBufferToBase64(ab) {
  const bytes = new Uint8Array(ab);
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return btoa(bin);
}
async function fileToUprightBase64(file) {
  let contentType = 'image/jpeg';
  let ab = await uprightJpegBytes(file, 2000, 0.85);
  if (!ab) { ab = await file.arrayBuffer(); contentType = file.type || 'image/jpeg'; }
  return { base64: arrayBufferToBase64(ab), contentType };
}

const STATUS_META = {
  reported:     { label: 'Reported',     color: '#f59e0b' },
  lodged:       { label: 'Lodged',       color: '#3b82f6' },
  acknowledged: { label: 'Acknowledged', color: '#8b5cf6' },
  settled:      { label: 'Settled',      color: '#22c55e' },
  closed:       { label: 'Closed',       color: '#6b7280' },
};
const STATUS_ORDER = ['reported', 'lodged', 'acknowledged', 'settled', 'closed'];

const labelStyle = { fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5 };
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)', width: '100%' };
const th = { padding: '9px 10px', textAlign: 'left', fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' };
const td = { padding: '10px', borderTop: '1px solid var(--hp-border)', fontSize: '.86rem', verticalAlign: 'middle' };

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.reported;
  return (
    <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: '.7rem', fontWeight: 700, color: '#fff', background: m.color, whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  );
}

const triggerDownload = (bytes, filename, type = 'application/pdf') => {
  const blob = new Blob([bytes], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

// NO DRAG-TO-MAIL HERE, DELIBERATELY.
// Two attempts at dragging an attachment out of the browser into Mail both failed in
// Paul's hands: the first embedded a link (the image arrived stale, because these URLs
// are admin-gated and the signed URL behind them dies in 120s), and the DownloadURL-only
// version didn't attach at all. Chromium's drag-out is finicky and — the real problem —
// it is a native OS interaction that cannot be exercised from here, so it was being
// shipped on reasoning rather than evidence. "Download all" does the same job, works
// everywhere, and is verifiable. If drag-out is ever revisited, it needs testing on the
// actual machine first.

// Pull an attachment's real bytes down through the admin-gated function. Used by
// "Download all" so every file lands in ~/Downloads ready to drag in together.
const downloadAttachment = async (att) => {
  const r = await claimsApi('getAttachmentBytes', { id: att.id });
  if (!r.success) throw new Error(r.error || 'could not fetch');
  const bin = atob(r.base64 || '');
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  triggerDownload(bytes, r.filename || att.filename || 'attachment', r.contentType || att.contentType || 'application/octet-stream');
};

// ── Prepare-email dialog ───────────────────────────────────────────────────────
// "Prepare, you send manually": assembles recipient/subject/body + a checklist of the
// attachments to add from your mail client, then stamps the timeline. No auto-send.
function PrepareEmailModal({ claim, attachments, onClose, onLogged, addNotification }) {
  const [to, setTo] = useState(CLAIMS_EMAIL);
  const [dlAll, setDlAll] = useState(false);

  // Fetch every attachment to ~/Downloads so they can be dragged into the email in one
  // go. Sequential + spaced: Chrome throttles (and prompts about) rapid multi-downloads.
  const downloadAll = async () => {
    setDlAll(true);
    let ok = 0, bad = 0;
    for (const a of attachments) {
      try { await downloadAttachment(a); ok++; } catch (_) { bad++; }
      await new Promise((r) => setTimeout(r, 350));
    }
    setDlAll(false);
    addNotification?.(
      bad ? `Downloaded ${ok} of ${attachments.length} — ${bad} failed` : `Downloaded ${ok} file${ok !== 1 ? 's' : ''} — drag them into your email from Downloads`,
      bad ? 'error' : 'success',
    );
  };
  const veh = claim.rego || 'vehicle';
  const subject = `Motor claim — ${veh} — ${fmtDate(claim.incidentDate)}${claim.claimRef ? ` — claim ${claim.claimRef}` : ''}`;
  const tp = claim.thirdParty || {};
  // Addressed to the broker, who lodges with Allianz for us — so it asks them to lodge,
  // rather than talking to Allianz as if they were reading it.
  const bodyLines = [
    'Hello Marsh Team,',
    '',
    `Please find attached our accident report and supporting documents. Could you please lodge the following motor fleet claim with Allianz (${INSURED_NAME}, policy ${POLICY_NO}):`,
    '',
    `  • Vehicle: ${claim.rego || '—'}`,
    `  • Driver: ${[claim.driverName, claim.driverLogin && `(${claim.driverLogin})`].filter(Boolean).join(' ') || '—'}`,
    `  • Date / time: ${[fmtDate(claim.incidentDate), claim.incidentTime].filter(Boolean).join(' · ') || '—'}`,
    `  • Location: ${claim.location || '—'}`,
    claim.claimRef ? `  • Claim number: ${claim.claimRef}` : '  • Claim number: (please advise)',
    '',
    'What happened:',
    claim.description || '—',
    '',
    (tp.name || tp.rego) ? `Third party: ${[tp.name, tp.rego, tp.insurer].filter(Boolean).join(' · ')}` : '',
    '',
    'Kind regards,',
    'Hollypaul Fleet',
  ].filter((l) => l !== undefined);
  const body = bodyLines.join('\n');

  const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const copy = (txt) => { try { navigator.clipboard.writeText(txt); } catch (_) { /* noop */ } };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 1100, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 40, overflowY: 'auto' }} onClick={onClose}>
      <div style={{ width: 640, maxWidth: '94%', background: 'var(--hp-bg)', borderRadius: 12, border: '1px solid var(--hp-border)', margin: '0 12px 40px', padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}><Mail size={18} style={{ color: 'var(--hp-gold)' }} /> Prepare email to Allianz</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: '.82rem', color: 'var(--hp-dim)' }}>
          This assembles the message — you send it from your own mail client and attach the files below. Nothing is sent automatically.
        </p>

        <label style={labelStyle}>To</label>
        <input className="input" style={{ ...inputStyle, marginBottom: 12 }} value={to} onChange={(e) => setTo(e.target.value)} />

        <label style={labelStyle}>Subject</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input className="input" style={inputStyle} value={subject} readOnly />
          <button className="btn btn-secondary btn-sm" onClick={() => copy(subject)}>Copy</button>
        </div>

        <label style={labelStyle}>Body</label>
        <textarea className="input" style={{ ...inputStyle, minHeight: 180, fontFamily: 'inherit', marginBottom: 8 }} value={body} readOnly />
        <button className="btn btn-secondary btn-sm" style={{ marginBottom: 16 }} onClick={() => copy(body)}><ClipboardCheck size={13} /> Copy body</button>

        <div style={{ borderTop: '1px solid var(--hp-border)', paddingTop: 14, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>Attach these ({attachments.length})</div>
            {attachments.length > 0 && (
              <button className="btn btn-primary btn-sm" onClick={downloadAll} disabled={dlAll}>
                {dlAll ? <><Loader size={12} /> Downloading…</> : <><Download size={12} /> Download all ({attachments.length})</>}
              </button>
            )}
          </div>
          {attachments.length === 0 ? (
            <div style={{ fontSize: '.82rem', color: 'var(--hp-amber, #f59e0b)' }}>No attachments yet — generate the summary PDF and/or fill the Allianz form first.</div>
          ) : (
            <>
              {/* mailto: cannot carry attachments — the OS mail app only accepts a
                  recipient/subject/body from a link. So the files have to get there by
                  hand: drag them out of here, or Download all and drag them in together. */}
              <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(201,168,76,0.08)', fontSize: '.78rem', color: 'var(--hp-text)', marginBottom: 10, lineHeight: 1.5 }}>
                <strong>Attachments can't ride along on the mail link</strong> — a browser can only hand Mail the
                recipient, subject and body. Three steps:
                <br />1. <strong>Download all</strong> → the files land in your Downloads folder
                <br />2. <strong>Open in mail app</strong> → the message is written for you
                <br />3. In Downloads, select them all (⌘A) and drag them into the email
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {attachments.map((a, i) => {
                  const isImg = (a.contentType || '').startsWith('image/');
                  return (
                    <a
                      key={a.id} href={a.viewUrl} target="_blank" rel="noreferrer"
                      title={`${a.label || a.filename} — click to open`}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                        width: 92, padding: 6, borderRadius: 8, border: '1px solid var(--hp-border)',
                        textDecoration: 'none', color: 'var(--hp-dim)', fontSize: '.66rem',
                      }}
                    >
                      {isImg
                        ? <img src={a.viewUrl} alt="" style={{ width: 78, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                        : <div style={{ width: 78, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--hp-surface)', borderRadius: 4 }}><FileText size={22} style={{ color: 'var(--hp-gold)' }} /></div>}
                      <span style={{ textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                        {isImg ? `Photo ${i + 1}` : (a.kind === 'allianz_filled' ? 'Allianz form' : a.kind === 'summary' ? 'Summary' : (a.filename || 'Doc'))}
                      </span>
                    </a>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <a className="btn btn-primary" href={mailto} style={{ flex: 1, textDecoration: 'none' }} onClick={() => onLogged(to)}>
            <Mail size={14} /> Open in mail app
          </a>
          <button className="btn btn-secondary" onClick={() => onLogged(to)}>Mark prepared</button>
        </div>
      </div>
    </div>
  );
}

// ── Add / edit drawer ─────────────────────────────────────────────────────────
function ClaimDrawer({ claim, vehicles, personnel, currentUser, onClose, onSaved, addNotification }) {
  const editing = !!claim;
  const blank = {
    rego: '', driverLogin: '', driverName: '', status: 'reported', claimRef: '',
    incidentDate: todayLocalISO(), incidentTime: '', location: '', description: '',
    damage: '', injuries: '', policeNotified: false, policeAttended: false, policeRef: '',
    tpName: '', tpPhone: '', tpRego: '', tpMake: '', tpModel: '', tpInsurer: '', tpPolicy: '', tpNotes: '',
  };
  const fromClaim = (c) => ({
    rego: c.rego || '', driverLogin: c.driverLogin || '', driverName: c.driverName || '',
    status: c.status || 'reported', claimRef: c.claimRef || '',
    incidentDate: c.incidentDate || '', incidentTime: c.incidentTime || '', location: c.location || '',
    description: c.description || '', damage: c.damage || '', injuries: c.injuries || '',
    policeNotified: !!c.policeNotified, policeAttended: !!c.policeAttended, policeRef: c.policeRef || '',
    tpName: c.thirdParty?.name || '', tpPhone: c.thirdParty?.phone || '', tpRego: c.thirdParty?.rego || '',
    tpMake: c.thirdParty?.make || '', tpModel: c.thirdParty?.model || '', tpInsurer: c.thirdParty?.insurer || '',
    tpPolicy: c.thirdParty?.policy || '', tpNotes: c.thirdParty?.notes || '',
  });
  const [form, setForm] = useState(claim ? fromClaim(claim) : blank);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [saving, setSaving] = useState(false);
  const [driverOverridden, setDriverOverridden] = useState(editing);
  const [suggestSource, setSuggestSource] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [busy, setBusy] = useState('');            // '' | 'photo' | 'allianz' | 'fill' | 'summary' | 'note'
  const [noteText, setNoteText] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const [dropOver, setDropOver] = useState(false);
  const claimId = claim?.id;

  const driverNameFromLogin = (login) => (personnel || []).find((p) => p.loginName?.toUpperCase() === (login || '').toUpperCase())?.name || '';

  const refresh = useCallback(async () => {
    if (!claimId) return;
    const r = await claimsApi('getClaim', { id: claimId });
    if (r.success) { setAttachments(r.attachments || []); setUpdates(r.updates || []); }
  }, [claimId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Automatically pull any scene photos the driver has added via the portal onto this
  // claim, on open. Runs after the first render (the drawer already shows what's there),
  // then re-fetches when it copies anything. Loops if a big first batch was capped
  // server-side. Silent when there's nothing new — the common case.
  useEffect(() => {
    if (!claimId) return undefined;
    let cancelled = false;
    (async () => {
      let total = 0;
      for (let i = 0; i < 4; i++) { // bounded: cap is 8/call, so ≤32 photos in one open
        const r = await claimsApi('syncDriverPhotos', { claimId });
        if (cancelled || !r.success || !r.copied) break;
        total += r.copied;
        if (!r.more) break;
      }
      if (!cancelled && total) {
        await refresh();
        addNotification?.(`Pulled ${total} new photo${total > 1 ? 's' : ''} from the driver's report`, 'success');
      }
    })();
    return () => { cancelled = true; };
  }, [claimId, refresh, addNotification]);

  // Auto-suggest the driver from custody history for (rego, incident date).
  useEffect(() => {
    if (driverOverridden || !form.rego || !form.incidentDate) return;
    let cancelled = false;
    assignApi('resolveDriver', { rego: form.rego, date: form.incidentDate }).then((r) => {
      if (cancelled || !r.success) return;
      const login = r.driverLogin || '';
      setForm((f) => ({ ...f, driverLogin: login, driverName: r.driverName || driverNameFromLogin(login) || f.driverName }));
      setSuggestSource(r.source || '');
    });
    return () => { cancelled = true; };
  }, [form.rego, form.incidentDate, driverOverridden]);

  const buildPayload = () => ({
    id: claimId,
    rego: form.rego, driverLogin: form.driverLogin, driverName: form.driverName,
    status: form.status, claimRef: form.claimRef,
    incidentDate: form.incidentDate, incidentTime: form.incidentTime, location: form.location,
    description: form.description, damage: form.damage, injuries: form.injuries,
    policeNotified: form.policeNotified, policeAttended: form.policeAttended, policeRef: form.policeRef,
    thirdParty: {
      name: form.tpName, phone: form.tpPhone, rego: form.tpRego, make: form.tpMake,
      model: form.tpModel, insurer: form.tpInsurer, policy: form.tpPolicy, notes: form.tpNotes,
    },
    createdBy: currentUser?.login || '',
  });

  const currentClaimForPdf = () => ({
    ...buildPayload(),
    thirdParty: buildPayload().thirdParty,
  });

  const save = async () => {
    if (!form.rego && !form.description) { addNotification?.('Add at least a vehicle or a description', 'error'); return; }
    setSaving(true);
    try {
      const r = await claimsApi('saveClaim', buildPayload());
      if (!r.success) { addNotification?.(`Save failed: ${r.error || 'unknown error'}`, 'error'); return; }
      addNotification?.(editing ? 'Claim updated' : 'Claim created', 'success');
      onSaved(r.id);
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setSaving(false); }
  };

  // ── Photos ──
  const addPhoto = async (file) => {
    if (!file || !claimId) return;
    if (!file.type.startsWith('image/')) { addNotification?.('Photos must be images', 'error'); return; }
    setBusy('photo');
    try {
      const { base64, contentType } = await fileToUprightBase64(file);
      const r = await claimsApi('addAttachment', { claimId, kind: 'photo', fileBase64: base64, contentType, filename: `photo_${Date.now()}.jpg`, label: 'Photo' });
      if (!r.success) { addNotification?.(`Upload failed: ${r.error || 'unknown error'}`, 'error'); return; }
      await refresh();
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };

  // ── Paste / drop anything onto the claim ──
  // One entry point for files arriving by ⌘V or drag-drop, so you can copy a PDF in
  // Finder (or screenshot a photo) and drop it straight on the claim. Works out what it
  // is rather than asking: a CLM002 files as the claim's completed Allianz form — which
  // is the round-trip that matters (download → add the sketch by hand → paste back) —
  // any other PDF files as a document, and images file as photos.
  const addFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!claimId || !list.length) return;
    setBusy('paste');
    let photos = 0, docs = 0, forms = 0, failed = 0;
    try {
      for (const file of list) {
        const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
        const isImg = (file.type || '').startsWith('image/');
        if (!isPdf && !isImg) { failed++; continue; }
        if (file.size > MAX_MB * 1024 * 1024) {
          addNotification?.(`"${file.name || 'file'}" is over ${MAX_MB} MB — too big to attach`, 'error');
          failed++; continue;
        }
        try {
          if (isImg) {
            const { base64, contentType } = await fileToUprightBase64(file);
            const r = await claimsApi('addAttachment', { claimId, kind: 'photo', fileBase64: base64, contentType, filename: file.name || `photo_${Date.now()}.jpg`, label: 'Photo' });
            if (!r.success) throw new Error(r.error);
            photos++;
          } else {
            const buf = await file.arrayBuffer();
            const bytes = new Uint8Array(buf);
            const isForm = await isClm002Pdf(bytes);
            const kind = isForm ? 'allianz_filled' : 'other';
            const label = isForm ? 'Allianz claim form (completed)' : (file.name || 'Document');
            const r = await claimsApi('addAttachment', { claimId, kind, fileBase64: arrayBufferToBase64(buf), contentType: 'application/pdf', filename: file.name || 'document.pdf', label });
            if (!r.success) throw new Error(r.error);
            if (isForm) forms++; else docs++;
          }
        } catch (e) { addNotification?.(`Could not attach "${file.name || 'file'}": ${e.message}`, 'error'); failed++; }
      }
      if (photos || docs || forms) {
        await refresh();
        const bits = [];
        if (forms) bits.push(`${forms} Allianz form${forms > 1 ? 's' : ''} (replaced the previous one)`);
        if (photos) bits.push(`${photos} photo${photos > 1 ? 's' : ''}`);
        if (docs) bits.push(`${docs} document${docs > 1 ? 's' : ''}`);
        addNotification?.(`Attached ${bits.join(' · ')}`, 'success');
      } else if (failed) {
        addNotification?.('Nothing attached — paste or drop a PDF or an image', 'error');
      }
    } finally { setBusy(''); }
  };

  // ⌘V anywhere in the drawer. Ignored while typing in a field, so pasting text into
  // the description doesn't get hijacked. Held in a ref so the listener is bound once
  // per claim rather than re-bound on every keystroke, while still seeing fresh state.
  const addFilesRef = useRef(addFiles);
  addFilesRef.current = addFiles;
  useEffect(() => {
    if (!claimId) return undefined;
    const onPaste = (e) => {
      // Only intercept when actual FILES are on the clipboard. A plain-text paste — into
      // the description, or anywhere else — must behave normally.
      const files = Array.from(e.clipboardData?.files || []);
      if (!files.length) return;
      e.preventDefault();
      addFilesRef.current(files);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [claimId]);

  // ── Allianz form: upload blank ──
  const uploadAllianz = async (file) => {
    if (!file || !claimId) return;
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (!isPdf) { addNotification?.('Upload the Allianz claim form as a PDF', 'error'); return; }
    if (file.size > MAX_MB * 1024 * 1024) { addNotification?.(`PDF too large — max ${MAX_MB} MB`, 'error'); return; }
    setBusy('allianz');
    try {
      const base64 = arrayBufferToBase64(await file.arrayBuffer());
      const r = await claimsApi('addAttachment', { claimId, kind: 'allianz_blank', fileBase64: base64, contentType: 'application/pdf', filename: file.name, label: 'Allianz form (blank)' });
      if (!r.success) { addNotification?.(`Upload failed: ${r.error || 'unknown error'}`, 'error'); return; }
      await refresh();
      addNotification?.('Allianz form uploaded — now click "Fill from claim details"', 'success');
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };

  // ── Allianz form: fill it and hand it straight back ──
  // The blank CLM002 ships with the app (public/forms), so there's nothing to upload —
  // press the button, get a completed form to send. An uploaded blank still wins if one
  // is attached, so a revised form from Allianz can be dropped in without a deploy.
  const fillAllianz = async () => {
    if (!claimId) return;
    setBusy('fill');
    try {
      let bytes;
      const blankAtt = attachments.find((a) => a.kind === 'allianz_blank');
      if (blankAtt) {
        const g = await claimsApi('getAttachmentBytes', { id: blankAtt.id });
        if (!g.success) { addNotification?.(`Could not load the uploaded form: ${g.error}`, 'error'); return; }
        bytes = base64ToBytes(g.base64);
      } else {
        const res = await fetch(CLM002_URL);
        if (!res.ok) throw new Error(`could not load the bundled claim form (${res.status})`);
        bytes = new Uint8Array(await res.arrayBuffer());
      }

      // The real CLM002 is flat (no AcroForm) → fillAllianzForm draws a coordinate
      // overlay. Feed it the fleet vehicle and the driver's record so the vehicle and
      // driver sections fill from what we already hold rather than by hand.
      const veh = (vehicles || []).find((v) => (v.rego || '').toUpperCase() === (form.rego || '').toUpperCase());
      // `personnel` is drivers-sb listDriversFull — it already carries address/phone/company.
      const p = (personnel || []).find((x) => (x.loginName || '').toUpperCase() === (form.driverLogin || '').toUpperCase());
      const drv = p ? { address: p.address, mobile: p.phone, company_name: p.companyName, type: p.type } : null;
      const { bytes: filled, filled: n } = await fillAllianzForm(bytes, currentClaimForPdf(), veh, drv);

      // Download first — this is the thing you actually need in your hand.
      const fname = `Allianz-claim-${form.rego || 'claim'}-${form.incidentDate || todayLocalISO()}.pdf`;
      triggerDownload(filled, fname);

      // Then keep a copy on the claim so the record shows exactly what was sent.
      const r = await claimsApi('addAttachment', { claimId, kind: 'allianz_filled', fileBase64: bytesToBase64(filled), contentType: 'application/pdf', filename: fname, label: 'Allianz claim form (completed)' });
      if (!r.success) { addNotification?.(`Downloaded, but could not file a copy on the claim: ${r.error || 'unknown error'}`, 'error'); return; }
      await refresh();
      addNotification?.(`Allianz form completed (${n} fields) and downloaded. Check it, then add the sketch by hand — that section can't be auto-filled.`, 'success');
    } catch (e) { addNotification?.(`Could not fill the form: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };

  // ── Branded summary PDF ──
  const generateSummary = async () => {
    if (!claimId) return;
    setBusy('summary');
    try {
      const bytes = await buildAccidentSummaryPdfBytes(currentClaimForPdf());
      const b64 = bytesToBase64(bytes);
      const r = await claimsApi('addAttachment', { claimId, kind: 'summary', fileBase64: b64, contentType: 'application/pdf', filename: `accident-summary-${form.rego || 'claim'}.pdf`, label: 'Accident summary' });
      if (!r.success) { addNotification?.(`Save failed: ${r.error || 'unknown error'}`, 'error'); return; }
      await refresh();
      addNotification?.('Summary PDF generated', 'success');
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };

  const removeAttachment = async (att) => {
    if (!window.confirm('Remove this file?')) return;
    const r = await claimsApi('deleteAttachment', { id: att.id });
    if (r.success) await refresh();
    else addNotification?.(`Delete failed: ${r.error || 'unknown error'}`, 'error');
  };

  const addNote = async () => {
    if (!noteText.trim() || !claimId) return;
    setBusy('note');
    try {
      const r = await claimsApi('addNote', { claimId, body: noteText.trim() });
      if (r.success) { setUpdates(r.updates || []); setNoteText(''); }
      else addNotification?.(`Could not add note: ${r.error || 'unknown error'}`, 'error');
    } finally { setBusy(''); }
  };

  const logEmailPrepared = async (to) => {
    setEmailOpen(false);
    if (!claimId) return;
    await claimsApi('logEmailPrepared', { claimId, to });
    await refresh();
  };

  const photos = attachments.filter((a) => a.kind === 'photo');
  const docs = attachments.filter((a) => a.kind !== 'photo');
  // Everything except the blank template is something Allianz might want — including a
  // document you pasted in yourself (a quote, a police report, the hand-finished form).
  const sendable = attachments.filter((a) => a.kind !== 'allianz_blank');

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div
        style={{
          width: 620, maxWidth: '100%', background: 'var(--hp-bg)', height: '100%',
          overflowY: 'auto', padding: 26, position: 'relative',
          borderLeft: dropOver ? '2px solid var(--hp-gold)' : '1px solid var(--hp-border)',
        }}
        onClick={(e) => e.stopPropagation()}
        onDragOver={(e) => { if (claimId) { e.preventDefault(); setDropOver(true); } }}
        onDragLeave={(e) => { if (e.currentTarget === e.target) setDropOver(false); }}
        onDrop={(e) => { if (!claimId) return; e.preventDefault(); setDropOver(false); addFiles(e.dataTransfer?.files); }}
      >
        {/* Drop anywhere on the claim, not just on a small target. */}
        {dropOver && (
          <div style={{
            position: 'absolute', inset: 8, border: '2px dashed var(--hp-gold)', borderRadius: 12,
            background: 'rgba(201,168,76,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none', zIndex: 5, fontWeight: 700, color: 'var(--hp-gold)',
          }}>
            Drop to attach to this claim
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 }}>Accident claim</div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{editing ? `${claim.rego || 'Claim'} — ${fmtDate(claim.incidentDate)}` : 'New accident claim'}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        {!editing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18, padding: '10px 12px', borderRadius: 8, background: 'rgba(201,168,76,0.08)', fontSize: '.82rem', color: 'var(--hp-dim)' }}>
            <AlertTriangle size={14} style={{ color: 'var(--hp-gold)' }} /> Save the claim first — then you can add photos, the Allianz form and the summary PDF.
          </div>
        )}

        {/* Core fields */}
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={labelStyle}>Vehicle (rego)</label>
            <input className="input" style={{ ...inputStyle, textTransform: 'uppercase' }} list="claims-rego-list" value={form.rego} onChange={(e) => { set('rego', e.target.value.toUpperCase()); setDriverOverridden(false); }} placeholder="Select from fleet" />
            <datalist id="claims-rego-list">
              {(vehicles || []).map((v) => <option key={v.rego} value={v.rego}>{[v.make, v.model].filter(Boolean).join(' ')}</option>)}
            </datalist>
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select className="input" style={inputStyle} value={form.status} onChange={(e) => set('status', e.target.value)}>
              {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Incident date</label>
            <input type="date" className="input" style={inputStyle} value={form.incidentDate} onChange={(e) => { set('incidentDate', e.target.value); setDriverOverridden(false); }} />
          </div>
          <div>
            <label style={labelStyle}>Time</label>
            <input type="time" className="input" style={inputStyle} value={form.incidentTime} onChange={(e) => set('incidentTime', e.target.value)} />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Driver at the wheel</label>
            <select className="input" style={inputStyle} value={form.driverLogin} onChange={(e) => { setDriverOverridden(true); const login = e.target.value; setForm((f) => ({ ...f, driverLogin: login, driverName: driverNameFromLogin(login) })); }}>
              <option value="">— none / enter a name below —</option>
              {(personnel || []).map((p) => <option key={p.loginName} value={p.loginName}>{p.loginName} — {p.name}</option>)}
            </select>
            {suggestSource && !driverOverridden && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: '.78rem', color: suggestSource === 'none' ? 'var(--hp-amber, #f59e0b)' : 'var(--hp-dim)' }}>
                <Sparkles size={12} /> {suggestSource === 'none' ? 'No custody record for that date — set the driver manually.' : 'Suggested from the vehicle’s custody history — confirm it’s right.'}
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Driver name</label>
            <input className="input" style={inputStyle} value={form.driverName} onChange={(e) => { setDriverOverridden(true); set('driverName', e.target.value); }} placeholder="Free text" />
          </div>
          <div>
            <label style={labelStyle}>Allianz claim #</label>
            <input className="input" style={inputStyle} value={form.claimRef} onChange={(e) => set('claimRef', e.target.value)} placeholder="When it arrives" />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Location</label>
            <input className="input" style={inputStyle} value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Street / suburb / intersection" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>What happened</label>
            <textarea className="input" style={{ ...inputStyle, minHeight: 84, fontFamily: 'inherit' }} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Describe the incident" />
          </div>
          <div>
            <label style={labelStyle}>Damage to our vehicle</label>
            <textarea className="input" style={{ ...inputStyle, minHeight: 56, fontFamily: 'inherit' }} value={form.damage} onChange={(e) => set('damage', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Injuries / people</label>
            <textarea className="input" style={{ ...inputStyle, minHeight: 56, fontFamily: 'inherit' }} value={form.injuries} onChange={(e) => set('injuries', e.target.value)} />
          </div>
          {/* Allianz asks these as two separate questions — ringing 000 notifies them
              without a car necessarily turning up. Don't collapse them. */}
          <div>
            <label style={labelStyle}>Police</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.86rem', color: 'var(--hp-text)', paddingTop: 6 }}>
              <input
                type="checkbox"
                checked={form.policeNotified}
                onChange={(e) => setForm((f) => ({ ...f, policeNotified: e.target.checked, ...(e.target.checked ? {} : { policeAttended: false, policeRef: '' }) }))}
              /> Notified (e.g. called 000)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.86rem', color: form.policeNotified ? 'var(--hp-text)' : 'var(--hp-muted)', paddingTop: 6 }}>
              <input type="checkbox" checked={form.policeAttended} disabled={!form.policeNotified} onChange={(e) => set('policeAttended', e.target.checked)} /> Attended the scene
            </label>
          </div>
          <div>
            <label style={labelStyle}>Police event / report #</label>
            <input className="input" style={inputStyle} value={form.policeRef} onChange={(e) => set('policeRef', e.target.value)} disabled={!form.policeNotified} placeholder="From the 000 call or the officer" />
          </div>
        </div>

        {/* Third party */}
        <div style={{ marginTop: 20, borderTop: '1px solid var(--hp-border)', paddingTop: 16 }}>
          <div style={{ ...labelStyle, marginBottom: 10 }}>Third party (if any)</div>
          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={labelStyle}>Name</label><input className="input" style={inputStyle} value={form.tpName} onChange={(e) => set('tpName', e.target.value)} /></div>
            <div><label style={labelStyle}>Phone</label><input className="input" style={inputStyle} value={form.tpPhone} onChange={(e) => set('tpPhone', e.target.value)} /></div>
            <div><label style={labelStyle}>Their rego</label><input className="input" style={{ ...inputStyle, textTransform: 'uppercase' }} value={form.tpRego} onChange={(e) => set('tpRego', e.target.value.toUpperCase())} /></div>
            <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div><label style={labelStyle}>Make</label><input className="input" style={inputStyle} value={form.tpMake} onChange={(e) => set('tpMake', e.target.value)} /></div>
              <div><label style={labelStyle}>Model</label><input className="input" style={inputStyle} value={form.tpModel} onChange={(e) => set('tpModel', e.target.value)} /></div>
            </div>
            <div><label style={labelStyle}>Their insurer</label><input className="input" style={inputStyle} value={form.tpInsurer} onChange={(e) => set('tpInsurer', e.target.value)} /></div>
            <div><label style={labelStyle}>Their policy #</label><input className="input" style={inputStyle} value={form.tpPolicy} onChange={(e) => set('tpPolicy', e.target.value)} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Notes</label><input className="input" style={inputStyle} value={form.tpNotes} onChange={(e) => set('tpNotes', e.target.value)} /></div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={save} disabled={saving}>
            {saving ? <><Loader size={14} /> Saving…</> : <><Save size={14} /> {editing ? 'Save changes' : 'Save claim'}</>}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>

        {/* Everything below needs a saved claim */}
        {editing && (
          <>
            {/* Photos */}
            <div style={{ marginTop: 26, borderTop: '1px solid var(--hp-border)', paddingTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={labelStyle}>Photos ({photos.length})</span>
                <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
                  <input type="file" accept="image/*" capture="environment" onChange={(e) => { addPhoto(e.target.files?.[0]); e.target.value = ''; }} style={{ display: 'none' }} disabled={busy === 'photo'} />
                  {busy === 'photo' ? <><Loader size={13} /> Adding…</> : <><Camera size={13} /> Add photo</>}
                </label>
              </div>
              {photos.length === 0 ? (
                <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>No photos yet.</div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {photos.map((a, i) => (
                      <div key={a.id} style={{ position: 'relative' }}>
                        <a
                          href={a.viewUrl} target="_blank" rel="noreferrer"
                          title="Click to open full size"
                          style={{ display: 'block' }}
                        >
                          <img
                            src={a.viewUrl} alt={a.label || `photo ${i + 1}`}
                            style={{ width: 104, height: 104, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hp-border)', display: 'block' }}
                          />
                        </a>
                        <button
                          onClick={() => removeAttachment(a)}
                          title="Remove"
                          style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: 999, border: 'none', background: 'var(--hp-red, #ef4444)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginTop: 8 }}>
                    Click a photo to open it full size. To send them, use <strong>Prepare email</strong> →
                    <strong> Download all</strong> — these links are private and expire, so a link on its own
                    would be no use to the recipient.
                  </div>
                </>
              )}
            </div>

            {/* Documents: Allianz form + summary */}
            <div style={{ marginTop: 22, borderTop: '1px solid var(--hp-border)', paddingTop: 16 }}>
              <span style={{ ...labelStyle, marginBottom: 10 }}>Claim documents</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                <button className="btn btn-primary btn-sm" onClick={fillAllianz} disabled={busy === 'fill'}>
                  {busy === 'fill' ? <><Loader size={13} /> Completing…</> : <><Download size={13} /> Complete &amp; download Allianz form</>}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={generateSummary} disabled={busy === 'summary'}>
                  {busy === 'summary' ? <><Loader size={13} /> Generating…</> : <><FileText size={13} /> Generate summary PDF</>}
                </button>
              </div>
              <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginBottom: 12 }}>
                The blank Allianz form ships with the app — no upload needed. It fills from this claim,
                the fleet record and the driver's file, downloads, and files a copy here.
                {' '}
                <label style={{ color: 'var(--hp-gold)', cursor: 'pointer', textDecoration: 'underline' }}>
                  <input type="file" accept="application/pdf" onChange={(e) => { uploadAllianz(e.target.files?.[0]); e.target.value = ''; }} style={{ display: 'none' }} disabled={busy === 'allianz'} />
                  {busy === 'allianz' ? 'Uploading…' : 'Use a different blank form'}
                </label>
                {attachments.some((a) => a.kind === 'allianz_blank') && <span style={{ color: 'var(--hp-amber, #f59e0b)' }}> · using your uploaded blank</span>}
              </div>

              {/* Paste / drop target. The round-trip: download the form, add the sketch by
                  hand, then ⌘V it straight back onto the claim. */}
              <label
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  border: '1px dashed var(--hp-border)', borderRadius: 10, padding: '12px 14px',
                  marginBottom: 12, cursor: 'pointer', fontSize: '.8rem', color: 'var(--hp-dim)',
                }}
              >
                <input
                  type="file" accept="application/pdf,image/*" multiple style={{ display: 'none' }}
                  disabled={busy === 'paste'}
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                />
                {busy === 'paste'
                  ? <><Loader size={13} /> Attaching…</>
                  : <><ClipboardPaste size={14} style={{ color: 'var(--hp-gold)' }} />
                      <span><strong style={{ color: 'var(--hp-text)' }}>Paste (⌘V), drop, or click</strong> to attach — a completed Allianz form replaces the one on file; photos and other PDFs are added.</span></>}
              </label>
              {docs.length === 0 ? (
                <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>Nothing generated yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {docs.map((a) => (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.82rem' }}>
                      <a href={a.viewUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm"><ExternalLink size={12} /> {a.label || a.filename}</a>
                      <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto', color: 'var(--hp-red, #ef4444)' }} onClick={() => removeAttachment(a)}><Trash2 size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Prepare email */}
            <div style={{ marginTop: 22 }}>
              <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={() => setEmailOpen(true)}>
                <Mail size={14} /> Prepare email to Allianz
              </button>
            </div>

            {/* Timeline */}
            <div style={{ marginTop: 26, borderTop: '1px solid var(--hp-border)', paddingTop: 16 }}>
              <span style={{ ...labelStyle, marginBottom: 10 }}>Updates & timeline</span>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input className="input" style={inputStyle} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note (e.g. spoke to assessor)…" onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }} />
                <button className="btn btn-secondary btn-sm" onClick={addNote} disabled={busy === 'note' || !noteText.trim()}>
                  {busy === 'note' ? <Loader size={13} /> : <MessageSquarePlus size={13} />}
                </button>
              </div>
              {updates.length === 0 ? (
                <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>No updates yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {updates.map((u) => (
                    <div key={u.id} style={{ display: 'flex', gap: 10, fontSize: '.82rem' }}>
                      <div style={{ width: 4, borderRadius: 4, background: 'var(--hp-border)', flexShrink: 0 }} />
                      <div>
                        <div style={{ color: 'var(--hp-text)' }}>{u.body}</div>
                        <div style={{ color: 'var(--hp-dim)', fontSize: '.72rem', marginTop: 2 }}>{fmtWhen(u.createdAt)}{u.author ? ` · ${u.author}` : ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {emailOpen && (
          <PrepareEmailModal
            claim={{ ...buildPayload(), thirdParty: buildPayload().thirdParty }}
            attachments={sendable}
            onClose={() => setEmailOpen(false)}
            onLogged={logEmailPrepared}
            addNotification={addNotification}
          />
        )}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function VehicleClaimsPage() {
  const { personnel, vehicles, currentUser, addNotification } = useApp();
  const isAdmin = currentUser?.role === 'admin';
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // null | { claim } | { claim: null }
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await claimsApi('listClaims', statusFilter ? { status: statusFilter } : {});
      if (r.success) setClaims(r.claims);
      else addNotification?.(`Could not load claims: ${r.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setLoading(false); }
  }, [addNotification, statusFilter]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const remove = async (c) => {
    if (!window.confirm(`Delete this claim for ${c.rego || 'vehicle'}? This removes its photos and PDFs too.`)) return;
    const r = await claimsApi('deleteClaim', { id: c.id });
    if (r.success) { addNotification?.('Claim deleted', 'success'); load(); }
    else addNotification?.(`Delete failed: ${r.error || 'unknown error'}`, 'error');
  };

  if (!isAdmin) {
    return (
      <>
        <div className="page-header"><div className="page-header-left"><h2>Vehicle Accident / Insurance Claims</h2></div></div>
        <div className="page-body">
          <div className="card" style={{ padding: 28, display: 'flex', alignItems: 'center', gap: 12 }}>
            <ShieldAlert size={22} color="var(--hp-gold)" />
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Admins only</div>
              <div style={{ color: 'var(--hp-dim)', fontSize: '.88rem' }}>The claims register is restricted to Paul and Jayda.</div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const openCount = claims.filter((c) => c.status !== 'closed' && c.status !== 'settled').length;
  const reportedCount = claims.filter((c) => c.status === 'reported').length;

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Car size={22} style={{ color: 'var(--hp-gold)' }} /> Vehicle Accident / Insurance Claims</h2>
          <p>{claims.length} recorded · {openCount} open{reportedCount ? ` · ${reportedCount} awaiting review` : ''} · Allianz Motor Fleet ({POLICY_NO})</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="input" style={{ ...inputStyle, width: 'auto' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
          <button className="btn btn-primary" onClick={() => setDrawer({ claim: null })}><Plus size={14} /> New claim</button>
        </div>
      </div>

      <div className="page-body">
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 26, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading claims…</div>
          ) : claims.length === 0 ? (
            <div style={{ padding: 26, color: 'var(--hp-dim)' }}>No claims yet. Click <strong>New claim</strong> to log an accident, or a driver can report one from the portal.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                <thead>
                  <tr>
                    <th style={th}>Vehicle</th>
                    <th style={th}>Driver</th>
                    <th style={th}>Incident date</th>
                    <th style={th}>Location</th>
                    <th style={th}>Status</th>
                    <th style={th}>Allianz claim #</th>
                    <th style={th}>Source</th>
                    <th style={{ ...th, textAlign: 'right' }} />
                  </tr>
                </thead>
                <tbody>
                  {claims.map((c) => (
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setDrawer({ claim: c })}>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{c.rego || '—'}</td>
                      <td style={td}>{c.driverName || c.driverLogin || <span style={{ color: 'var(--hp-amber, #f59e0b)' }}>unknown</span>}</td>
                      <td style={td}>{fmtDate(c.incidentDate)}{c.incidentTime ? <span style={{ color: 'var(--hp-dim)', fontSize: '.78rem' }}> · {c.incidentTime}</span> : ''}</td>
                      <td style={{ ...td, maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.location}>{c.location || '—'}</td>
                      <td style={td}><StatusPill status={c.status} /></td>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>{c.claimRef || <span style={{ color: 'var(--hp-dim)' }}>—</span>}</td>
                      <td style={td}>{c.reportedVia === 'driver' ? <span style={{ fontSize: '.74rem', color: 'var(--hp-gold)' }}>driver</span> : <span style={{ fontSize: '.74rem', color: 'var(--hp-dim)' }}>admin</span>}</td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setDrawer({ claim: c })}>Open</button>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--hp-red, #ef4444)' }} onClick={() => remove(c)}><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: '.8rem', color: 'var(--hp-dim)' }}>
          <AlertTriangle size={13} /> Data and images are retained per claim in a private store. The driver at the wheel is suggested from the vehicle's custody history — always confirm.
        </div>
      </div>

      {drawer && (
        <ClaimDrawer
          claim={drawer.claim}
          vehicles={vehicles}
          personnel={personnel}
          currentUser={currentUser}
          addNotification={addNotification}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); load(); }}
        />
      )}
    </>
  );
}
